import { ChevronRight } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useObjectCategories } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/shared/components/ui';
import type { RouteProp } from '@/shared/types/route-prop';
import {
  getObjectCategoryIcon,
  normalizeObjectCategoryIconKey,
} from '@/shared/utils/object-category-icons';

interface SidebarLinkProps {
  route: RouteProp;
  isActive?: boolean;
  currentPath: string;
}

const DynamicCategoryChildren = ({
  currentPath,
  basePath,
}: {
  currentPath: string;
  basePath: string;
}) => {
  const [searchParams] = useSearchParams();
  const currentCategoryFilter = searchParams.get('category');

  const { data: categoriesData } = useObjectCategories({
    limit: 50,
    sort: 'name',
  });

  const categories = categoriesData?.docs || [];

  if (categories.length === 0) return null;

  return (
    <>
      {categories.map((category) => {
        const categoryPath = `${basePath}?category=${category.id}`;
        const isActive = currentPath.startsWith(basePath) && currentCategoryFilter === category.id;
        const normalizedIconKey = normalizeObjectCategoryIconKey(category.icon) ?? '';
        const Icon = getObjectCategoryIcon(category.icon);
        return (
          <SidebarMenuSubItem key={category.id}>
            <SidebarMenuSubButton asChild data-active={isActive}>
              <Link to={categoryPath}>
                <Icon
                  className={`size-4 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                  data-testid={`sidebar-object-category-icon-${category.id}`}
                  data-icon-key={normalizedIconKey}
                />
                <span className="truncate">{category.name}</span>
              </Link>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
    </>
  );
};

export const SidebarLink = ({ route, isActive, currentPath }: SidebarLinkProps) => {
  const { t } = useIntl();
  const [searchParams] = useSearchParams();
  const hasChildren = route.children && route.children.length > 0;
  const hasDynamicChildren = route.dynamicChildren === 'object-categories';

  // Check if we're on the objects page with a category filter
  const currentCategoryFilter = searchParams.get('category');
  const isOnObjectsWithCategory =
    currentPath.startsWith(route.to) && hasDynamicChildren && !!currentCategoryFilter;

  // For routes with children, check if any child is active
  // Don't activate if viewing objects with a category filter (category item should be active instead)
  const isAnyChildActive =
    (hasChildren || hasDynamicChildren) &&
    currentPath.startsWith(route.to) &&
    !isOnObjectsWithCategory;

  // For dynamic category children, check if any category is active
  const isCategoryActive = hasDynamicChildren && isOnObjectsWithCategory;

  if (hasChildren || hasDynamicChildren) {
    // Objekte (dynamicChildren) immer ausgeklappt, andere nur wenn aktiv
    const shouldDefaultOpen = hasDynamicChildren || isAnyChildActive || isCategoryActive;

    return (
      <Collapsible asChild defaultOpen={shouldDefaultOpen} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={t(route.label as unknown as never) as string}
              data-active={false}
            >
              {route.icon && <route.icon className="size-[18px] text-muted-foreground" />}
              <span className="truncate">{t(route.label as unknown as never)}</span>
              <ChevronRight className="ml-auto size-[14px] transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {route.children?.map((child) => {
                // For "All Objects" child, only active when on objects page without category filter
                const isChildActive =
                  hasDynamicChildren && child.to === route.to
                    ? currentPath.startsWith(child.to) && !currentCategoryFilter
                    : currentPath === child.to || currentPath.startsWith(`${child.to}/`);

                return (
                  <SidebarMenuSubItem key={child.to}>
                    <SidebarMenuSubButton asChild data-active={isChildActive}>
                      <Link to={child.to}>
                        {child.icon && (
                          <child.icon
                            className={`size-4 ${isChildActive ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        )}
                        <span className="truncate">{t(child.label as unknown as never)}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
              {hasDynamicChildren && (
                <DynamicCategoryChildren currentPath={currentPath} basePath={route.to} />
              )}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  const itemIsActive = isActive ?? currentPath.startsWith(route.to);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={t(route.label as unknown as never) as string}
        data-active={itemIsActive}
      >
        <Link to={route.to} className="min-w-0">
          {route.icon && (
            <route.icon
              className={`size-[18px] ${itemIsActive ? 'text-primary' : 'text-muted-foreground'}`}
            />
          )}
          <span className="truncate">{t(route.label as unknown as never)}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
